/* AdminOutcomesPanel — embedded panel inside AdminPanel for verifying outcomes.
   Backend: GET /api/engagement/admin/outcomes · POST /api/engagement/admin/outcomes/:id/status
   Restricted to user.role === 'admin'.
*/
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, XCircle, Eye, ExternalLink, Loader2, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const STATUS_TONE = {
  submitted: 'bg-gray-100 text-gray-700',
  verified:  'bg-amber-100 text-amber-800',
  published: 'bg-emerald-100 text-emerald-800',
  rejected:  'bg-rose-100 text-rose-700',
};

const AdminOutcomesPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/engagement/admin/outcomes`);
      setItems(res.data?.outcomes || []);
    } catch (_) { /* noop */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/engagement/admin/outcomes/${id}/status`, { status });
      await load();
    } catch (_) { /* noop */ }
    setBusyId(null);
  };

  return (
    <section className="bg-white rounded-3xl border border-gray-200 p-5 md:p-7" data-testid="admin-outcomes-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg md:text-xl font-extrabold text-gray-900">Outcomes verification</h2>
          <p className="text-xs text-gray-500">Move submissions through submitted → verified → published.</p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="admin-outcomes-refresh"
          className="inline-flex items-center gap-1.5 text-sm font-bold text-teal-700 hover:text-teal-900"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 size={20} className="animate-spin text-teal-600" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-10 text-sm text-gray-500" data-testid="admin-outcomes-empty">
          No outcomes submitted yet. Once a verified student submits one on /outcomes, it lands here.
        </div>
      ) : (
        <ul className="space-y-3" data-testid="admin-outcomes-list">
          {items.map((o) => (
            <li key={o.id} className="bg-gray-50 border border-gray-200 rounded-2xl p-4">
              <div className="flex items-start justify-between flex-wrap gap-2 mb-2">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-extrabold tracking-widest text-teal-700 uppercase mb-0.5">{o.role_name}</div>
                  <h3 className="text-[15px] font-extrabold text-gray-900">{o.headline}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {o.user_name} · {o.user_id?.slice(0, 12)}… · {(o.created_at || '').slice(0, 10)}
                  </p>
                </div>
                <span className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full ${STATUS_TONE[o.status] || STATUS_TONE.submitted}`} data-testid={`outcome-status-${o.id}`}>
                  {o.status}
                </span>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed mb-3 whitespace-pre-wrap">{o.body}</p>
              {o.proof_url && (
                <a href={o.proof_url} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1.5 text-xs text-teal-700 hover:text-teal-900 font-bold mb-3"
                   data-testid={`outcome-proof-${o.id}`}>
                  <ExternalLink size={12} /> Proof URL
                </a>
              )}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={busyId === o.id || o.status === 'verified'}
                  onClick={() => setStatus(o.id, 'verified')}
                  data-testid={`outcome-verify-${o.id}`}
                  className="text-xs font-bold inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-100 text-amber-800 hover:bg-amber-200 disabled:opacity-50"
                >
                  <Eye size={12} /> Mark verified
                </button>
                <button
                  type="button"
                  disabled={busyId === o.id || o.status === 'published'}
                  onClick={() => setStatus(o.id, 'published')}
                  data-testid={`outcome-publish-${o.id}`}
                  className="text-xs font-bold inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-800 hover:bg-emerald-200 disabled:opacity-50"
                >
                  <CheckCircle2 size={12} /> Publish
                </button>
                <button
                  type="button"
                  disabled={busyId === o.id || o.status === 'rejected'}
                  onClick={() => setStatus(o.id, 'rejected')}
                  data-testid={`outcome-reject-${o.id}`}
                  className="text-xs font-bold inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-rose-100 text-rose-800 hover:bg-rose-200 disabled:opacity-50"
                >
                  <XCircle size={12} /> Reject
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default AdminOutcomesPanel;
